import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, Badge, Spinner, XPBar } from "@/components/ui";
import Button from "@/components/ui/Button";
import { getShadow } from "@/components/ui/ShadowBadge";
import { getLevel } from "@/components/ui/XPBar";
import toast from "react-hot-toast";
import clsx from "clsx";

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["student", id],
    queryFn: () => api.get(`/students/${id}`).then((r) => r.data),
  });

  const { data: shadowData } = useQuery({
    queryKey: ["student", id, "shadow"],
    queryFn: () => api.get(`/students/${id}/shadow`).then((r) => r.data),
  });

  const { data: achData } = useQuery({
    queryKey: ["student", id, "achievements"],
    queryFn: () => api.get(`/achievements/student/${id}`).then((r) => r.data),
  });

  const awardXP = useMutation({
    mutationFn: (amount) => api.post(`/admin/students/${id}/award-xp`, { amount }),
    onSuccess: (res) => {
      toast.success(`✨ ${res.data.amount} XP vergeben!`);
      qc.invalidateQueries({ queryKey: ["student", id] });
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  const awardCoins = useMutation({
    mutationFn: (amount) => api.post(`/admin/students/${id}/award-coins`, { amount }),
    onSuccess: (res) => {
      toast.success(`🪙 ${res.data.amount} Coins vergeben!`);
      qc.invalidateQueries({ queryKey: ["student", id] });
    },
    onError: (err) => toast.error(err.response?.data?.error || "Fehler"),
  });

  if (isLoading) return <div className="flex justify-center pt-20"><Spinner size="lg" /></div>;

  const student = data?.student;
  const profile = student;
  const user    = student?.user;
  const xp      = profile?.xp || 0;
  const coins   = profile?.coins || 0;
  const level   = getLevel(xp);
  const shadow  = getShadow(xp);
  const shadowProfile = shadowData?.shadow;

  const XP_AMOUNTS    = [50, 100, 200, 500];
  const COIN_AMOUNTS  = [50, 100, 250, 500];

  return (
    <div className="space-y-5 animate-fade-in">
      <button onClick={() => navigate("/students")} className="text-gray-400 hover:text-white font-bold text-sm">← Zurück</button>

      {/* Header */}
      <Card className="p-5" glow>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-nexus-s2 border-2 border-violet-500/40 flex items-center justify-center text-4xl flex-shrink-0">
            {user?.avatar || "🦊"}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-black">{user?.name}</h2>
            <div className="flex gap-2 flex-wrap mt-1">
              <Badge color="purple">Lv.{level}</Badge>
              <span className="text-xs font-bold" style={{ color: shadow.color }}>{shadow.emoji} {shadow.name}</span>
            </div>
            <XPBar xp={xp} size="sm" className="mt-2" />
          </div>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { l: "XP", v: xp.toLocaleString("de-DE"), c: "text-violet-400" },
          { l: "Coins", v: `🪙 ${coins.toLocaleString("de-DE")}`, c: "text-yellow-400" },
          { l: "Streak", v: `🔥 ${profile?.streak || 0}`, c: "text-orange-400" },
        ].map((s) => (
          <Card key={s.l} className="p-3 text-center">
            <p className={clsx("font-black text-lg", s.c)}>{s.v}</p>
            <p className="text-[10px] text-gray-500 uppercase font-bold">{s.l}</p>
          </Card>
        ))}
      </div>

      {/* Shadow profile (admin only) */}
      {shadowProfile && (
        <Card className="p-4 border-violet-500/30">
          <h3 className="font-black text-sm mb-3 text-violet-400">🌑 Shadow-Profil (intern)</h3>
          <div className="space-y-2">
            {[
              ["Engagementrate", `${shadowProfile.engagementRate || 0}%`],
              ["Quiz-Erfolgsrate", `${shadowProfile.quizSuccessRate || 0}%`],
              ["Ø Antwortzeit", `${shadowProfile.avgResponseTime || 0}s`],
              ["Risiko-Flag", shadowProfile.riskFlag ? "⚠️ Ja" : "✅ Nein"],
              ["Notizen", shadowProfile.notes || "—"],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between items-center text-sm">
                <span className="text-gray-400">{k}</span>
                <span className="font-bold text-white">{v}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Award XP */}
      <Card className="p-4">
        <h3 className="font-black text-sm mb-3">✨ XP vergeben</h3>
        <div className="flex flex-wrap gap-2">
          {XP_AMOUNTS.map((amt) => (
            <Button key={amt} size="sm" variant="secondary" onClick={() => awardXP.mutate(amt)} loading={awardXP.isPending}>
              +{amt} XP
            </Button>
          ))}
        </div>
      </Card>

      {/* Award Coins */}
      <Card className="p-4">
        <h3 className="font-black text-sm mb-3">🪙 Coins vergeben</h3>
        <div className="flex flex-wrap gap-2">
          {COIN_AMOUNTS.map((amt) => (
            <Button key={amt} size="sm" variant="secondary" onClick={() => awardCoins.mutate(amt)} loading={awardCoins.isPending}>
              +{amt} 🪙
            </Button>
          ))}
        </div>
      </Card>

      {/* Recent achievements */}
      {achData?.achievements?.length > 0 && (
        <Card className="p-4">
          <h3 className="font-black text-sm mb-3">🏅 Achievements</h3>
          <div className="flex flex-wrap gap-2">
            {achData.achievements.map((a) => (
              <div key={a.id} className="flex items-center gap-1.5 bg-nexus-s2 border border-nexus-border rounded-xl px-2.5 py-1.5">
                <span className="text-sm">{a.achievement.icon}</span>
                <span className="text-xs font-bold">{a.achievement.title}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Quiz attempts */}
      {profile?.quizAttempts?.length > 0 && (
        <Card className="p-4">
          <h3 className="font-black text-sm mb-3">📚 Quiz-Versuche ({profile.quizAttempts.length})</h3>
          <div className="space-y-2">
            {profile.quizAttempts.slice(0, 5).map((a) => (
              <div key={a.id} className="flex items-center justify-between p-2.5 bg-nexus-s2 rounded-xl border border-nexus-border">
                <div>
                  <p className="font-bold text-sm">{a.quiz?.title || "Quiz"}</p>
                  <p className="text-[10px] text-gray-500">{new Date(a.createdAt).toLocaleDateString("de-DE")}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-violet-400">{a.score}/{a.totalQuestions}</p>
                  <p className="text-[10px] text-green-400">+{a.xpEarned} XP</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="pb-2" />
    </div>
  );
}
